import React, { useState } from 'react';
import { App, Button, Card, DatePicker, Form, Input, Modal, Segmented, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  AppstoreOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  ExperimentOutlined,
  InboxOutlined,
  PlusOutlined,
  SwapOutlined,
  UnorderedListOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import BulkActionsBar from '@/components/BulkActions/BulkActionsBar';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import ApplicationBoard from './ApplicationBoard';
import { useRecruitList } from './shared/useRecruitList';
import ReasonPrompt from './shared/ReasonPrompt';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { APPLICATION_STAGES, bulkSummary, CANDIDATE_SOURCES, exportRows, fmtDate, recruitErrorMessage, stageLabel } from './shared/recruitUi';

const { Text } = Typography;
const FILTERS = ['view', 'job_posting_id', 'status', 'owner_employee_id', 'department_id', 'source', 'date_from', 'date_to'] as const;

const EXPORT_COLS: Array<[string, string]> = [
  ['application_number', 'Application #'],
  ['candidate_number', 'Candidate #'],
  ['candidate_name', 'Candidate'],
  ['candidate_email', 'Email'],
  ['candidate_phone', 'Phone'],
  ['job_number', 'Job #'],
  ['job_title', 'Job'],
  ['status', 'Stage'],
  ['days_in_stage', 'Days in stage'],
  ['screening_score', 'Screening score'],
  ['source', 'Source'],
  ['applied_via', 'Applied via'],
  ['owner_name', 'Owner'],
  ['rejection_reason_code', 'Rejection reason'],
  ['created_at', 'Applied on'],
];

type BulkDialog = null | 'stage' | 'reject' | 'owner' | 'assessment';

const ApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const list = useRecruitList(FILTERS);
  const view = list.filters.view === 'board' ? 'board' : 'list';
  const [selected, setSelected] = useState<string[]>([]);
  const [dialog, setDialog] = useState<BulkDialog>(null);
  const [bulkForm] = Form.useForm();

  const listFilters = { ...list.apiParams, view: undefined };
  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.applications(listFilters),
    queryFn: () => hrmsService.pageApplications(listFilters),
    placeholderData: keepPreviousData,
    enabled: view === 'list',
  });
  const { data: jobs } = useQuery({
    queryKey: hrmsKeys.jobs({ picker: 1 }),
    queryFn: () => hrmsService.pageRecruitJobs({ page: 1, limit: 200 }),
    staleTime: 60_000,
  });
  const { data: templates = [] } = useQuery({
    queryKey: hrmsKeys.assessmentTemplates(),
    queryFn: () => hrmsService.listAssessmentTemplates(),
    enabled: dialog === 'assessment',
  });

  const refresh = async () => {
    setSelected([]);
    setDialog(null);
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.applications() });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.applicationBoard() });
  };

  const report = (label: string, r: any) => {
    const s = bulkSummary(label, r);
    if (s.type === 'success') message.success(s.text);
    else message.warning(s.text);
  };

  const runBulk = async (label: string, fn: () => Promise<any>) => {
    try {
      report(label, await fn());
      await refresh();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    }
  };

  const exportAll = async () => {
    const all = await hrmsService.pageApplications({ ...listFilters, page: 1, limit: 5000 });
    await exportRows('applications', EXPORT_COLS, all.rows);
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Application #',
      dataIndex: 'application_number',
      key: 'application_number',
      width: 140,
      fixed: 'left',
      sorter: true,
      sortOrder: list.sortOrderFor('application_number'),
      render: (v: string, r) => <a onClick={() => navigate(`/hr/applications/${r.id}`)}>{v}</a>,
    },
    {
      title: 'Candidate',
      dataIndex: 'candidate_name',
      key: 'candidate_name',
      sorter: true,
      sortOrder: list.sortOrderFor('candidate_name'),
      ellipsis: true,
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => navigate(`/hr/candidates/${r.candidate_id}`)}>{v}</a>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {r.candidate_email || r.candidate_phone || r.candidate_number}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Job',
      dataIndex: 'job_title',
      key: 'job_title',
      sorter: true,
      sortOrder: list.sortOrderFor('job_title'),
      ellipsis: true,
    },
    {
      title: 'Stage',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      sorter: true,
      sortOrder: list.sortOrderFor('status'),
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{stageLabel(s)}</Tag>,
    },
    {
      title: 'In stage',
      dataIndex: 'days_in_stage',
      key: 'days_in_stage',
      width: 90,
      sorter: true,
      sortOrder: list.sortOrderFor('days_in_stage'),
      render: (v) => <Tag color={Number(v) > 7 ? 'orange' : 'default'}>{v ?? 0}d</Tag>,
    },
    {
      title: 'Score',
      dataIndex: 'screening_score',
      key: 'screening_score',
      width: 80,
      sorter: true,
      sortOrder: list.sortOrderFor('screening_score'),
      render: (v) => (v != null ? Number(v) : '—'),
    },
    { title: 'Source', dataIndex: 'source', width: 100 },
    { title: 'Owner', dataIndex: 'owner_name', width: 150, ellipsis: true, render: (v) => v || <Text type="secondary">Unassigned</Text> },
    { title: 'Applied', dataIndex: 'created_at', width: 110, render: (v) => fmtDate(v) },
  ];

  const jobOptions = (jobs?.rows || []).map((j: any) => ({ value: j.id, label: `${j.job_number} — ${j.title}` }));

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Applications"
          subtitle="Hiring pipeline — screen, move and reject in bulk"
          primaryActions={
            <Space>
              <Segmented
                value={view}
                onChange={(v) => list.update({ view: v === 'board' ? 'board' : undefined }, false)}
                options={[
                  { value: 'list', icon: <UnorderedListOutlined />, label: 'List' },
                  { value: 'board', icon: <AppstoreOutlined />, label: 'Board' },
                ]}
              />
              {view === 'list' && (
                <Button icon={<DownloadOutlined />} onClick={exportAll}>
                  Export
                </Button>
              )}
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/applications/new')}>
                Add application
              </Button>
            </Space>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search allowClear placeholder="Candidate, email, job, number" defaultValue={list.q} onSearch={list.setQ} style={{ width: 250 }} />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Job"
            style={{ width: 260 }}
            value={list.filters.job_posting_id}
            onChange={(v) => list.setFilter('job_posting_id', v)}
            options={jobOptions}
          />
          {view === 'list' && (
            <Select
              allowClear
              placeholder="Stage"
              style={{ width: 140 }}
              value={list.filters.status}
              onChange={(v) => list.setFilter('status', v)}
              options={APPLICATION_STAGES.map((v) => ({ value: v, label: stageLabel(v) }))}
            />
          )}
          <RecruitPeoplePicker
            placeholder="Owner"
            style={{ width: 200 }}
            value={list.filters.owner_employee_id}
            onChange={(v) => list.setFilter('owner_employee_id', v as string)}
          />
          <DepartmentSelect
            allowClear
            placeholder="Department"
            style={{ width: 220 }}
            value={list.filters.department_id}
            onChange={(v) => list.setFilter('department_id', v)}
          />
          {view === 'list' && (
            <>
              <Select
                allowClear
                placeholder="Source"
                style={{ width: 130 }}
                value={list.filters.source}
                onChange={(v) => list.setFilter('source', v)}
                options={CANDIDATE_SOURCES.map((v) => ({ value: v, label: v }))}
              />
              <DatePicker.RangePicker
                value={list.filters.date_from ? [dayjs(list.filters.date_from), dayjs(list.filters.date_to)] : null}
                onChange={(r) =>
                  list.update({
                    date_from: r?.[0]?.format('YYYY-MM-DD'),
                    date_to: r?.[1]?.format('YYYY-MM-DD'),
                  })
                }
              />
            </>
          )}
          {list.activeFilterCount > (list.filters.view ? 1 : 0) && (
            <Button type="link" onClick={() => list.update({ job_posting_id: undefined, status: undefined, owner_employee_id: undefined, department_id: undefined, source: undefined, date_from: undefined, date_to: undefined, q: undefined })}>
              Clear filters
            </Button>
          )}
        </Space>
      </Card>

      {view === 'board' ? (
        <ApplicationBoard
          filters={{
            job_posting_id: list.filters.job_posting_id,
            owner_employee_id: list.filters.owner_employee_id,
            department_id: list.filters.department_id,
            q: list.q || undefined,
          }}
        />
      ) : (
        <Table
          rowKey="id"
          loading={isFetching}
          columns={columns}
          dataSource={data?.rows || []}
          scroll={{ x: 1250 }}
          pagination={list.pagination(data?.total || 0)}
          onChange={list.onTableChange}
          rowSelection={{ selectedRowKeys: selected, onChange: (k) => setSelected(k as string[]), preserveSelectedRowKeys: true }}
        />
      )}

      <BulkActionsBar
        selectedCount={selected.length}
        totalCount={data?.total || 0}
        selectedIds={selected}
        visible={view === 'list'}
        onClearSelection={() => setSelected([])}
        includeDefaultActions={false}
        actions={[
          { key: 'stage', label: 'Move stage', icon: <SwapOutlined />, selfReporting: true, onClick: () => { bulkForm.resetFields(); setDialog('stage'); } },
          { key: 'reject', label: 'Reject', icon: <CloseCircleOutlined />, danger: true, selfReporting: true, onClick: () => setDialog('reject') },
          {
            key: 'pool',
            label: 'Move to talent pool',
            icon: <InboxOutlined />,
            selfReporting: true,
            confirm: true,
            onClick: (ids) => runBulk('Moved to talent pool', () => hrmsService.bulkApplicationStatus({ ids, status: 'TalentPool' })),
          },
          { key: 'owner', label: 'Assign owner', icon: <UserSwitchOutlined />, selfReporting: true, onClick: () => { bulkForm.resetFields(); setDialog('owner'); } },
          { key: 'assessment', label: 'Assign assessment', icon: <ExperimentOutlined />, selfReporting: true, onClick: () => { bulkForm.resetFields(); setDialog('assessment'); } },
          {
            key: 'export',
            label: 'Export selected',
            icon: <DownloadOutlined />,
            selfReporting: true,
            onClick: (ids) => exportRows('applications-selected', EXPORT_COLS, (data?.rows || []).filter((r: any) => ids.includes(r.id))),
          },
        ]}
      />

      <Modal
        open={dialog === 'stage' || dialog === 'owner' || dialog === 'assessment'}
        title={
          dialog === 'stage'
            ? `Move ${selected.length} application(s)`
            : dialog === 'owner'
              ? `Assign owner to ${selected.length} application(s)`
              : `Assign assessment to ${selected.length} candidate(s)`
        }
        onCancel={() => setDialog(null)}
        destroyOnClose
        onOk={async () => {
          const v = await bulkForm.validateFields();
          if (dialog === 'stage') {
            await runBulk(`Moved to ${stageLabel(v.status)}`, () => hrmsService.bulkApplicationStatus({ ids: selected, status: v.status }));
          } else if (dialog === 'owner') {
            await runBulk('Owner assigned', () => hrmsService.bulkApplicationOwner({ ids: selected, owner_employee_id: v.owner_employee_id || null }));
          } else {
            await runBulk('Assessment assigned', () =>
              hrmsService.bulkApplicationAssessment({
                ids: selected,
                template_id: v.template_id,
                scheduled_at: v.scheduled_at ? v.scheduled_at.toISOString() : undefined,
                evaluator_employee_id: v.evaluator_employee_id,
                notify_candidate: false,
              })
            );
          }
        }}
      >
        <Form form={bulkForm} layout="vertical" preserve={false}>
          {dialog === 'stage' && (
            <Form.Item name="status" label="New stage" rules={[{ required: true }]} extra="Rows whose current stage cannot move there are skipped and listed.">
              <Select options={['Screening', 'Interview', 'Applied'].map((v) => ({ value: v, label: stageLabel(v) }))} />
            </Form.Item>
          )}
          {dialog === 'owner' && (
            <Form.Item name="owner_employee_id" label="Owner (leave empty to unassign)">
              <RecruitPeoplePicker />
            </Form.Item>
          )}
          {dialog === 'assessment' && (
            <>
              <Form.Item name="template_id" label="Template" rules={[{ required: true }]}>
                <Select options={templates.filter((t: any) => t.is_active !== false).map((t: any) => ({ value: t.id, label: t.template_name }))} />
              </Form.Item>
              <Form.Item name="scheduled_at" label="Scheduled">
                <DatePicker showTime style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="evaluator_employee_id" label="Evaluator">
                <RecruitPeoplePicker />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <ReasonPrompt
        open={dialog === 'reject'}
        title={`Reject ${selected.length} application(s)`}
        okText="Reject"
        appliesTo="Application"
        showNotify
        onCancel={() => setDialog(null)}
        onSubmit={(v) =>
          runBulk('Rejected', () =>
            hrmsService.bulkApplicationStatus({
              ids: selected,
              status: 'Rejected',
              rejection_reason_code: v.reason_code,
              rejection_notes: v.notes,
              notify_candidate: v.notify_candidate,
            })
          )
        }
      />
      {view === 'list' && data && data.total > 0 && selected.length === 0 && (
        <Tooltip title="Select rows to move, reject or assign in bulk">
          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: tick rows to act on many applications at once.
          </Text>
        </Tooltip>
      )}
    </WorkspaceLayout>
  );
};

export default ApplicationsPage;
